import {
  getDefaultTaiwanLandlineAreaCode,
  parseTaiwanLandlineValue,
  taiwanLandlineAreaCodes,
} from '@/lib/taiwan-landline'
import { useState } from 'react'
import type { AnswerInputProps } from '../QuestionRenderer'

export function TaiwanLandlineInput({
  question,
  value,
  onChange,
  onSubmit,
}: AnswerInputProps<string>) {
  const fallbackAreaCode = getDefaultTaiwanLandlineAreaCode(
    question.settings.taiwanLandlineAreaCode,
  )
  const parsedValue = parseTaiwanLandlineValue(value, fallbackAreaCode)
  const [areaCode, setAreaCode] = useState(parsedValue.areaCode)
  const [number, setNumber] = useState(parsedValue.number)
  const [extension, setExtension] = useState(parsedValue.extension)
  const fieldId = `taiwan-landline-${question.id}`

  function emit(nextAreaCode: string, nextNumber: string, nextExtension: string) {
    const baseValue = nextNumber ? `${nextAreaCode}-${nextNumber}` : nextAreaCode
    onChange(nextExtension ? `${baseValue}#${nextExtension}` : baseValue)
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="grid gap-3 sm:grid-cols-[minmax(10rem,14rem)_minmax(0,1fr)_minmax(8rem,10rem)]">
        <div className="flex flex-col gap-2">
          <label htmlFor={`${fieldId}-area-code`} className="text-sm font-medium text-slate-700">
            市話區碼
          </label>
          <select
            id={`${fieldId}-area-code`}
            value={areaCode}
            onChange={(event) => {
              const nextAreaCode = event.target.value
              setAreaCode(nextAreaCode)
              emit(nextAreaCode, number, extension)
            }}
            className="h-12 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {taiwanLandlineAreaCodes.map((option) => (
              <option key={option.code} value={option.code}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <label htmlFor={`${fieldId}-number`} className="text-sm font-medium text-slate-700">
            市話號碼
          </label>
          <input
            id={`${fieldId}-number`}
            value={number}
            pattern={
              typeof question.settings.validationRegex === 'string'
                ? question.settings.validationRegex
                : undefined
            }
            onChange={(event) => {
              const nextNumber = event.target.value
              setNumber(nextNumber)
              emit(areaCode, nextNumber, extension)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onSubmit()
            }}
            className="h-12 min-w-0 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            placeholder={String(question.settings.placeholder ?? '2345-6789')}
          />
        </div>

        <div className="flex min-w-0 flex-col gap-2">
          <label htmlFor={`${fieldId}-extension`} className="text-sm font-medium text-slate-700">
            分機
          </label>
          <input
            id={`${fieldId}-extension`}
            value={extension}
            inputMode="numeric"
            maxLength={6}
            onChange={(event) => {
              const nextExtension = event.target.value.replace(/\D/g, '').slice(0, 6)
              setExtension(nextExtension)
              emit(areaCode, number, nextExtension)
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') onSubmit()
            }}
            className="h-12 min-w-0 rounded-md border border-slate-200 bg-white px-3 text-sm outline-none focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
            placeholder="123"
          />
        </div>
      </div>

      {question.settings.errorMessage ? (
        <div className="text-sm text-rose-500">{question.settings.errorMessage}</div>
      ) : null}
    </div>
  )
}
